import {
  type CheckInOutType,
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import {
  type gqlV2,
  useDashboard,
  useInfiniteQuery,
  useOwnerPortalContact,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import {
  type Maybe,
  day,
  ensure,
  hasValue,
  isDate,
  toTitleCase,
} from '@vrplatform/utils';
import { Center, Group } from '@mantine/core';
import type {
  activeStatus_enum,
  booking_channel_bool_exp,
} from '@vrplatform/graphql';
import {
  getListingName,
  orderByListing,
  whereConnectionStatusDefault,
  whereListings,
  type whereReservations,
} from '@vrplatform/ui-common';
import { useMemo, useState } from 'react';
import { useReservationTableFilter } from './useReservationTableFilter';

export const ReservationTableFilter = ({
  hide,
  showMonthSelect,
}: {
  hide?: ('connection' | 'booking-channel' | 'date' | 'status')[];
  showMonthSelect?: boolean;
}) => {
  const { filter, setFilter } = useReservationTableFilter();
  const [dashboard] = useDashboard();
  const [{ isGL: GL }] = useTeam();

  return (
    <Group gap="xs">
      <Filter.Search
        value={filter.search || ''}
        setValue={(v) => setFilter({ search: v || null })}
      />
      {!hide?.includes('date') && <Date />}
      {showMonthSelect && <Month />}
      {!hide?.includes('status') && <Status />}
      {GL && dashboard === 'propertyManager' && <PaidStatus />}
      {!hide?.includes('connection') && <Connection />}
      {!hide?.includes('booking-channel') && <BookingChannel />}
      {dashboard === 'propertyManager' && !GL && <ListingGroup />}
      <Listing />
      {dashboard === 'propertyManager' && <ListingStatus />}
    </Group>
  );
};

const Month = () => {
  const { filter, setFilter } = useReservationTableFilter();

  type Month = Date | null | undefined;

  const date = useMemo<Month>(() => {
    const defaultDate = day().startOf('month').toDate();

    if (!filter.date) return defaultDate;

    const [v] = filter.date.split('...');

    if (!isDate(v)) return defaultDate;

    return day(v).startOf('month').toDate();
  }, [filter.date]);

  return (
    <Filter.Month
      value={date}
      setValue={(value) => {
        value &&
          setFilter({
            date: [
              day(value).startOf('month').yyyymmdd(),
              day(value).endOf('month').yyyymmdd(),
            ].join('...'),
          });
      }}
      type="default"
      label="Month"
    />
  );
};

const Date = () => {
  const { filter, setFilter } = useReservationTableFilter();

  return (
    <Filter.Date
      value={filter.date || undefined}
      setValue={(value) => {
        setFilter({
          date:
            value
              ?.filter(hasValue)
              .map((date) => day(date).yyyymmdd())
              .join('...') || null,
        });
      }}
      dateType={(filter.dateType as CheckInOutType) || 'all'}
      setDateType={(v) => setFilter({ dateType: v })}
    />
  );
};

const Status = () => {
  const { filter, setFilter } = useReservationTableFilter();
  const [dashboard] = useDashboard();

  const options: SelectItem[] = useMemo<SelectItem[]>(() => {
    return ensure<(SelectItem | undefined)[]>([
      {
        label: 'Booked',
        value: 'booked',
        icon: (
          <Center
            sx={(theme) => ({
              width: 8,
              height: 8,
              backgroundColor: theme.colors.green[6],
              borderRadius: '50%',
              alignSelf: 'center',
            })}
          />
        ),
      },
      {
        label: 'Canceled',
        value: 'canceled',
        icon: (
          <Center
            sx={(theme) => ({
              width: 8,
              height: 8,
              backgroundColor: theme.colors.orange[6],
              borderRadius: '50%',
              alignSelf: 'center',
            })}
          />
        ),
      },
      dashboard === 'propertyManager'
        ? {
            label: 'Deleted',
            value: 'deleted',
            icon: (
              <Center
                sx={(theme) => ({
                  width: 8,
                  height: 8,
                  backgroundColor: theme.colors.red[6],
                  borderRadius: '50%',
                  alignSelf: 'center',
                })}
              />
            ),
          }
        : undefined,
    ]).filter(hasValue);
  }, [dashboard]);

  const value: SelectItem | null =
    options.find((o) => o.value === filter.status) || null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ status: v?.value || null })}
      label="Status"
      type="single"
      hideIcon
      data={{
        options,
      }}
    />
  );
};

const PaidStatus = () => {
  const { filter, setFilter } = useReservationTableFilter();

  const options: SelectItem<
    NonNullable<Parameters<typeof whereReservations>[0]['paidStatus']>
  >[] = [
    // {
    //   label: 'Not paid',
    //   value: 'unpaid',
    //   icon: (
    //     <Center
    //       sx={(theme) => ({
    //         width: 8,
    //         height: 8,
    //         backgroundColor: theme.colors.orange[6],
    //         borderRadius: '50%',
    //         alignSelf: 'center',
    //       })}
    //     />
    //   ),
    // },
    {
      label: 'Unpaid',
      value: 'unpaid',
      icon: (
        <Center
          sx={(theme) => ({
            width: 8,
            height: 8,
            backgroundColor: theme.colors.red[6],
            borderRadius: '50%',
            alignSelf: 'center',
          })}
        />
      ),
    },
    {
      label: 'Paid fully',
      value: 'paid',
      icon: (
        <Center
          sx={(theme) => ({
            width: 8,
            height: 8,
            backgroundColor: theme.colors.green[6],
            borderRadius: '50%',
            alignSelf: 'center',
          })}
        />
      ),
    },
    // {
    //   label: 'Overpaid',
    //   value: 'overpaid',
    //   icon: (
    //     <Center
    //       sx={(theme) => ({
    //         width: 8,
    //         height: 8,
    //         backgroundColor: theme.colors.red[6],
    //         borderRadius: '50%',
    //         alignSelf: 'center',
    //       })}
    //     />
    //   ),
    // },
  ];

  const value: SelectItem | null =
    options.find((o) => o.value === filter.paidStatus) || null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ paidStatus: v?.value || null })}
      label="Payment Status"
      type="single"
      hideIcon
      data={{
        options,
      }}
    />
  );
};

const ListingStatus = () => {
  const [{ isGL: GL }] = useTeam();

  const { filter, setFilter } = useReservationTableFilter();

  return (
    <Filter.Status
      value={(filter.listingStatus as Maybe<activeStatus_enum>) || null}
      setValue={(v) => setFilter({ listingStatus: v || null })}
      label="Listing Status"
      inactiveLabel={GL ? 'Deactivated' : 'Disabled'}
    />
  );
};

const Connection = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = useReservationTableFilter();
  const [search, setSearch] = useState('');
  const gate = useSelectQueryGate({ value: filter.connectionIds });

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      const where: gqlV2.connection_bool_exp = {
        tenantId: { _eq: teamId },
        reservations: {},
        app: {
          id: { _neq: 'finalytic' },
        },
        status: whereConnectionStatusDefault,
        _or: search
          ? [
              {
                name: { _ilike: `%${search}%` },
              },
            ]
          : undefined,
      };

      const list = q
        .connection({
          where,
          order_by: [{ name: 'asc' }],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: res.name || 'Missing connection name',
        }));

      const aggregate =
        q.connectionAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: ['reservations', 'connections'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  const { data } = useQuery(
    (q, { connectionIds }) => {
      const value = q
        .connection({ where: { id: { _in: connectionIds } } })
        .map<SelectItem>((item) => ({
          label: item.name || '',
          value: item.id,
        }));

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: ['reservations', 'connections'],
      keepPreviousData: true,
      variables: {
        connectionIds: (filter.connectionIds || []).filter(hasValue),
      },
    }
  );

  const value = gate.hasValue ? data?.value || [] : [];

  return (
    <Filter.Select
      value={value}
      setValue={(v) =>
        setFilter({ connectionIds: v.length ? v.map((i) => i.value) : null })
      }
      label="Connection"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};

const Listing = () => {
  const [dashboard] = useDashboard();
  const { filter, setFilter } = useReservationTableFilter();
  const { contactId: ownerPortalContactId } = useOwnerPortalContact();
  const [{ isGL: GL, id: teamId }] = useTeam();

  const [search, setSearch] = useState('');
  const gate = useSelectQueryGate({ value: filter.listingIds });
  const shouldPrefetchOptions = dashboard === 'owner' && !search.trim();
  const shouldFetchOptions = gate.shouldFetchOptions || shouldPrefetchOptions;

  const queryData = useInfiniteQuery(
    (
      q,
      { teamId, search, dashboard, GL, ownerPortalContactId },
      { limit, offset }
    ) => {
      const where: gqlV2.listing_bool_exp = whereListings({
        tenantId: teamId,
        dashboard,
        search,
        ownerPortalContactId,
        GL,
      });

      const list = q
        .listings({
          where,
          order_by: [orderByListing],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: getListingName(res),
        }));

      const aggregate = q.listingAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !shouldFetchOptions,
      queryKey: ['reservations', 'listings'],
      variables: {
        teamId,
        search: search?.trim(),
        dashboard,
        ownerPortalContactId,
        GL,
      },
    }
  );

  const { data } = useQuery(
    (q, { listingIds }) => {
      const value = q
        .listings({ where: { id: { _in: listingIds } } })
        .map<SelectItem>((item) => ({
          label: getListingName(item),
          value: item.id,
        }));

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: ['reservations', 'listings'],
      keepPreviousData: true,
      variables: {
        listingIds: (filter.listingIds || []).filter(hasValue),
      },
    }
  );

  const value = gate.hasValue ? data?.value || [] : [];

  if (dashboard === 'owner' && shouldPrefetchOptions) {
    if (queryData.isLoading) return <LoadingIndicator size="18" />;
    if ((queryData.data?.pages[0]?.list.length ?? 0) < 2) return null;
  }

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ listingIds: v.map((i) => i.value) || null })}
      label="Listing"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};

const ListingGroup = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = useReservationTableFilter();
  const gate = useSelectQueryGate({ value: filter.listingCollectionId });

  const [search, setSearch] = useState('');

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      const where: gqlV2.listing_collection_bool_exp = {
        tenantId: { _eq: teamId },
        name: search
          ? { _ilike: `%${search}%` }
          : { _is_null: false, _neq: '' },
      };

      const list = q
        .listingCollections({
          where,
          order_by: [{ name: 'asc_nulls_last' }],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: res.name || 'No name',
        }));

      const aggregate =
        q.listingCollectionAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchAny,
      queryKey: ['reservations', 'listings'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  const values = queryData.data?.pages.flatMap((page) => page.list) || [];
  const value = gate.hasValue
    ? values.find((v) => v.value === filter.listingCollectionId) || null
    : null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ listingCollectionId: v?.value || null })}
      label="Listing Group"
      type="single"
      withinPortal
      noOptionsText="No listing groups found"
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};

const BookingChannel = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = useReservationTableFilter();

  const bookingChannels = filter.bookingChannels || [];
  const gate = useSelectQueryGate({ value: bookingChannels });

  const queryData = useQuery(
    (q, { teamId }) => {
      const where: booking_channel_bool_exp = {
        reservations: {
          tenantId: { _eq: teamId },
        },
      };

      return q
        .bookingChannels({
          where,
          order_by: [{ uniqueRef: 'asc' }],
        })
        .map<SelectItem>((res) => {
          return {
            value: res.id,
            label: toTitleCase(res.uniqueRef) || 'Missing booking channel',
          };
        });
    },
    {
      skip: !teamId || !gate.shouldFetchAny,
      queryKey: ['reservations', 'bookingChannels'],
      variables: {
        teamId,
      },
    }
  );

  const value = gate.hasValue
    ? queryData.data?.filter((entry) =>
        bookingChannels.includes(entry.value)
      ) || []
    : [];

  return (
    <Filter.Select
      value={value}
      setValue={(next) =>
        setFilter({ bookingChannels: next.map((item) => item.value) || null })
      }
      label="Booking Channel"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      data={{
        options: queryData.data || [],
        loading: queryData.isLoading,
        error: queryData.error,
        sort: null,
      }}
    />
  );
};
